/**
 * 时间螺线布局（ADR-0004，半径公式见 ADR-0010）。
 *
 * - 角度 = 这一天在一年里的位置：1 月 1 日在正上方，顺时针走一圈是一年，
 *   所以每年的同一天落在同一条射线上。
 * - 半径 = 从在一起那天起过了多久：缓坡对数螺线，r = r0·e^(growth·年) + linear·年。
 *   越往外圈距越宽：照片往往是近几年多、刚认识时少，外圈要放得下，内圈不必占一整圈的宽度。
 * - 同一天的照片按添加顺序排成黄金角向日葵，越早添的越靠近这一天的中心。
 *   新照片只会加在星团外缘，已有的星不会挪位置。
 *
 * 坐标系：星海在 XZ 平面，Y 朝上；从上往下看，正上方是 -Z。
 * 所有位置只由日期、添加顺序和照片 id 决定，两台设备算出来一样。
 */
import { dayOfYear, daysBetween, daysInYear, parseLocalDate, type LocalDate } from './dates.ts';

export interface SpiralParams {
  /** 在一起那天离中心多远 */
  r0: number;
  /** 对数部分：每过一年放大到 e^growth 倍 */
  growth: number;
  /** 线性补偿：每过一年再往外这么多，头几年的圈不至于挤在一起 */
  linear: number;
  /** 星团里相邻两颗星的间距 */
  cluster: number;
  /** 盘面厚度 */
  thickness: number;
  /** 盘面起伏的幅度 */
  warp: number;
}

/**
 * 用户给的参考是 8·e^(0.16·年) + 1.2·年（2026-10-02），这里整体放大一倍，在一起那天仍在 16：
 * 圈距从第一年的 5.2 慢慢放宽，第 5 年约 8、第 10 年约 14；第 10 年的半径约 103（等距时是 126）。
 */
export const SPIRAL: Readonly<SpiralParams> = { r0: 16, growth: 0.16, linear: 2.4, cluster: 0.17, thickness: 0.9, warp: 1.6 };

export interface Vec3 {
  x: number;
  y: number;
  z: number;
}

const TAU = Math.PI * 2;
/** 黄金角，约 137.5° */
export const GOLDEN_ANGLE = Math.PI * (3 - Math.sqrt(5));
const DAYS_PER_YEAR = 365.2425;

/** 这一天在一年里的角度（弧度，0 在正上方，顺时针增加） */
export function dayAngle(date: LocalDate): number {
  const { y } = parseLocalDate(date);
  return ((dayOfYear(date) - 0.5) / daysInYear(y)) * TAU;
}

/** 在一起以后第 years 年离中心多远（可以是小数）。在一起之前的日子平滑地收向中心，不会穿过去。 */
export function yearRadius(years: number, p: Readonly<SpiralParams> = SPIRAL): number {
  if (years >= 0) return p.r0 * Math.exp(p.growth * years) + p.linear * years;
  // 按在一起那天的斜率接着往里收（斜率连续，螺线在那天不折一下）；指数衰减，永远到不了中心
  return p.r0 * Math.exp(((p.r0 * p.growth + p.linear) / p.r0) * years);
}

/** 这一天离中心多远 */
export function dayRadius(date: LocalDate, since: LocalDate, p: Readonly<SpiralParams> = SPIRAL): number {
  return yearRadius(daysBetween(since, date) / DAYS_PER_YEAR, p);
}

/** 这一天的中心点 */
export function dayAnchor(date: LocalDate, since: LocalDate, p: Readonly<SpiralParams> = SPIRAL, out: Vec3 = { x: 0, y: 0, z: 0 }): Vec3 {
  const a = dayAngle(date), r = dayRadius(date, since, p);
  out.x = Math.sin(a) * r;
  out.z = -Math.cos(a) * r;
  out.y = Math.sin(a * 2 + r * 0.045) * p.warp;
  return out;
}

/** FNV-1a，把字符串稳定地映射到 [0, 1) */
export function hash01(s: string, salt = 0): number {
  let h = (0x811c9dc5 ^ salt) >>> 0;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  h ^= h >>> 15;
  h = Math.imul(h, 0x2c1b3c6d) >>> 0;
  h ^= h >>> 12;
  // 异或的结果是有符号 32 位整数，要转回无符号，否则会得到负数
  return (h >>> 0) / 4294967296;
}

/** 星团里第 i 颗星相对这一天中心的偏移；dayTurn 让不同日子的向日葵朝向不同 */
export function clusterOffset(i: number, dayTurn: number, jitter: number, p: Readonly<SpiralParams> = SPIRAL, out: Vec3 = { x: 0, y: 0, z: 0 }): Vec3 {
  const r = p.cluster * Math.sqrt(i + 0.5);
  const a = i * GOLDEN_ANGLE + dayTurn * TAU;
  out.x = Math.cos(a) * r;
  out.z = Math.sin(a) * r;
  out.y = (jitter - 0.5) * p.thickness;
  return out;
}

export interface StarSource {
  id: string;
  date: LocalDate;
}

/**
 * 算出每颗星的位置，每颗 3 个数写进 out。
 * items 要按添加顺序排好（先按 createdAt，再按 id），同一天里的先后由此决定。
 */
export function layoutStars(
  items: readonly StarSource[],
  since: LocalDate,
  p: Readonly<SpiralParams> = SPIRAL,
  out: Float32Array = new Float32Array(items.length * 3),
): Float32Array {
  const placer = new StarPlacer(since, p);
  const v: Vec3 = { x: 0, y: 0, z: 0 };
  for (let k = 0; k < items.length; k++) {
    placer.place(items[k]!, v);
    out[k * 3] = v.x;
    out[k * 3 + 1] = v.y;
    out[k * 3 + 2] = v.z;
  }
  return out;
}

/** 逐颗放星，记着每天已经放了几颗。实时添加照片时也用它，保证和整体重算的结果一致。 */
export class StarPlacer {
  private readonly days = new Map<string, { anchor: Vec3; turn: number; count: number }>();
  private readonly off: Vec3 = { x: 0, y: 0, z: 0 };
  readonly since: LocalDate;
  readonly params: Readonly<SpiralParams>;

  constructor(since: LocalDate, params: Readonly<SpiralParams> = SPIRAL) {
    this.since = since;
    this.params = params;
  }

  place(item: StarSource, out: Vec3 = { x: 0, y: 0, z: 0 }): Vec3 {
    let day = this.days.get(item.date);
    if (!day) {
      day = { anchor: dayAnchor(item.date, this.since, this.params), turn: hash01(item.date, 7), count: 0 };
      this.days.set(item.date, day);
    }
    clusterOffset(day.count++, day.turn, hash01(item.id, 3), this.params, this.off);
    out.x = day.anchor.x + this.off.x;
    out.y = day.anchor.y + this.off.y;
    out.z = day.anchor.z + this.off.z;
    return out;
  }

  /** 这一天已经放了几颗 */
  countOn(date: LocalDate): number {
    return this.days.get(date)?.count ?? 0;
  }
}
